import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  Grid,
  Avatar,
  Stepper,
  Step,
  StepLabel,
  Button,
  Chip,
  Alert,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Skeleton,
  useTheme,
  useMediaQuery,
  Collapse,
} from "@mui/material";
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import {
  LocalShipping,
  Schedule,
  ArrowBack,
  AssignmentTurnedIn,
  Inventory2,
  MoveToInbox,
  TaskAlt,
  Star,
  KeyboardArrowDown,
  KeyboardArrowUp,
  GetApp
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { getData, postData, postFileUpload } from "../../Webservice/Webservice";
import { API_URL } from "../../Utils/Url";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ReviewModal from "../../Components/ReviewModal/ReviewModal";

function OrderDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuType, setMenuType] = useState(null); // 'order' or 'item'
  const [selectedItem, setSelectedItem] = useState(null);
  const [cancelModal, setCancelModal] = useState({ open: false, reason: "" });
  const [downloading, setDownloading] = useState(false);

  const handleDownloadInvoice = async () => {
    try {
      setDownloading(true);
      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";

      const response = await fetch(`${API_URL}order/download-invoice/${order.invoiceNumber}`, {
        method: "GET",
        headers: {
          Authorization: token,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to download invoice");
      }

      // Read response as arrayBuffer to handle binary data correctly
      const buffer = await response.arrayBuffer();
      const byteArray = new Uint8Array(buffer);

      let blob;
      // Convert a small portion to check if it's hex or direct PDF
      const preview = new TextDecoder().decode(byteArray.slice(0, 20)).trim();

      // Check if it's a hex string (usually starts with hex chars, no %PDF)
      if (preview && /^[0-9a-fA-F]+$/.test(preview) && !preview.includes("%PDF")) {
        const hex = new TextDecoder().decode(byteArray).trim();
        const bytes = new Uint8Array(hex.match(/.{1,2}/g).map(byte => parseInt(byte, 16)));
        blob = new Blob([bytes], { type: "application/pdf" });
      } else {
        // Assume direct binary PDF data
        blob = new Blob([byteArray], { type: "application/pdf" });
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Invoice-${order.invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

    } catch (err) {
      console.error("Failed to download invoice:", err);
    } finally {
      setDownloading(false);
    }
  };
  const [returnModal, setReturnModal] = useState({
    open: false,
    reason: "",
    video: null,
  });
  const [reviewModal, setReviewModal] = useState({
    open: false,
    orderId: null,
  });
  const [expandedReturnId, setExpandedReturnId] = useState(null);

  const handleOpenReview = (orderId, item = null) => {
    handleMenuClose();
    if (item) setSelectedItem(item);
    setReviewModal({
      open: true,
      orderId,
    });
  };

  const handleCloseReview = () => {
    setReviewModal({
      open: false,
      orderId: null,
    });
    setSelectedItem(null);
    fetchOrderDetails();
  };

  const handleSubmitReview = async (reviewData) => {
    try {
      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";

      if (!token) {
        setError("Please login to submit review");
        return;
      }

      if (!selectedItem || !selectedItem.productId) {
        setError("No product selected for review");
        return;
      }

      const payload = {
        orderId: reviewModal.orderId,
        productId: selectedItem.productId,
        rating: String(reviewData.rating || 1),
        review: reviewData.review || ""
      };

      await postData("rating/add", payload, token, false);
    } catch (err) {
      console.error("Failed to submit review:", err);
    }
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const fetchOrderDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";

      if (!token) {
        setError("Please login to view order details");
        setLoading(false);
        return;
      }

      const response = await getData(`order/${id}`, null, token, false);

      if (response && response.order) {
        const orderData = response.order;

        // Transform items
        const transformedItems = orderData.items.map((item) => {
          const returnInfo = response.returnOrders?.find(
            (r) => r.productId?._id === item.product_id?._id
          );
          return {
            productId: item.product_id?._id,
            name: item.product_id?.title || "Product",
            image: item.product_id?.product_images?.[0] || "",
            price: item.price || 0,
            quantity: item.quantity || 1,
            review: item.review,
            returnInfo: returnInfo,
          };
        });

        // Format date
        const orderDate = new Date(orderData.createdAt);
        const formattedDate = orderDate.toLocaleDateString("en-GB", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        });

        // Map status history to stepper steps
        const statusMap = {
          confirmed: "Order Placed",
          packed: "Packed",
          shipped: "Shipped",
          "out for delivery": "Out for Delivery",
          delivered: "Delivered",
        };

        const steps =
          orderData.statusHistory
            ?.map(
              (statusItem) =>
                statusMap[statusItem.status] || statusItem.status
            )
            .filter((value, index, self) => self.indexOf(value) === index) ||
          [];

        // Find active step based on isActive status - find the latest one
        const activeStatus = [...(orderData.statusHistory || [])].reverse().find((s) => s.isActive);
        let activeStep = 0;
        if (activeStatus && steps.length > 0) {
          const activeStepLabel =
            statusMap[activeStatus.status] || activeStatus.status;
          activeStep = steps.findIndex((step) => step === activeStepLabel);
          if (activeStep === -1) activeStep = 0;
        }

        // Format shipping address
        const addressParts = [
          orderData.shippingAddress?.addressLine1,
          orderData.shippingAddress?.addressLine2,
          orderData.shippingAddress?.landmark,
          orderData.shippingAddress?.city,
          orderData.shippingAddress?.state,
          orderData.shippingAddress?.pincode,
          orderData.shippingAddress?.country,
        ].filter(Boolean);
        const formattedAddress = addressParts.join(", ");

        const transformedOrder = {
          id: `#${orderData.orderNumber}`,
          date: formattedDate,
          status: orderData.appStatus || "confirmed",
          tracking: orderData.orderNumber,
          total: orderData.totalAmount || 0,
          steps:
            steps.length > 0
              ? steps
              : ["Order Placed", "Processing", "Shipped", "Delivered"],
          activeStep: activeStep,
          shippingAddress: {
            name: orderData.shippingAddress?.name || "",
            address: formattedAddress,
            phone: orderData.shippingAddress?.mobile || "",
          },
          items: transformedItems,
          invoiceNumber: orderData.invoiceNumber,
          paymentStatus: orderData.paymentStatus,
          orderStatus: orderData.orderStatus,
          subtotal: orderData.subtotal,
          gsttotal: orderData.gsttotal,
          deliveryCharges: orderData.deliveryCharges,
          couponDiscount: orderData.couponDiscount,
        };

        setOrder(transformedOrder);
      } else {
        setError("Order not found");
      }
    } catch (err) {
      console.error("Error fetching order details:", err);
      setError(err.message || "Failed to fetch order details");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchOrderDetails();
    }
  }, [id, fetchOrderDetails]);

  const getStatusIcon = (status) => {
    switch (status) {
      case "confirmed":
        return <AssignmentTurnedIn />;
      case "packed":
        return <Inventory2 />;
      case "shipped":
        return <LocalShipping />;
      case "out-for-delivery":
      case "out for delivery":
        return <MoveToInbox />;
      case "delivered":
        return <TaskAlt />;
      default:
        return <Schedule />;
    }
  };

  // Skeleton component for order details
  const OrderDetailsSkeleton = () => (
    <Container
      maxWidth="md"
      sx={{
        py: { xs: 3, md: 6 },
        px: { xs: 2, sm: 3 },
        color: "black",
        fontFamily: "Rubik, sans-serif",
      }}
    >
      {/* Back Button Skeleton */}
      <Skeleton variant="rectangular" width={120} height={40} sx={{ borderRadius: "30px", mb: 3 }} />

      {/* Order Summary Skeleton */}
      <Paper
        elevation={2}
        sx={{
          borderRadius: "20px",
          backgroundColor: "white",
          p: { xs: 2.5, sm: 3, md: 4 },
          mb: 2,
          boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "row", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            gap: 2,
            mb: 3,
          }}
        >
          <Skeleton variant="circular" width={48} height={48} />
          <Box sx={{ flexGrow: 1 }}>
            <Skeleton variant="text" width="60%" height={24} />
            <Skeleton variant="text" width="40%" height={16} sx={{ mt: 0.5 }} />
          </Box>
          <Skeleton variant="circular" width={40} height={40} />
        </Box>

        <Divider sx={{ borderColor: "#e5e5e5", mb: 3 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Skeleton variant="text" width="30%" height={16} />
            <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: "18px", mt: 1 }} />
          </Grid>
        </Grid>
      </Paper>

      {/* Stepper Skeleton */}
      <Paper
        elevation={2}
        sx={{
          borderRadius: "20px",
          p: { xs: 2, sm: 3, md: 4 },
          mb: 2,
          backgroundColor: "white",
          boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
        }}
      >
        <Skeleton variant="text" width="40%" height={20} sx={{ mb: 2 }} />
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {[1, 2, 3, 4].map((step) => (
            <Box key={step} sx={{ display: "flex", flexDirection: "column", alignItems: "center", flex: 1 }}>
              <Skeleton variant="circular" width={30} height={30} />
              <Skeleton variant="text" width={60} height={12} sx={{ mt: 1 }} />
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Shipping Info Skeleton */}
      <Paper
        elevation={2}
        sx={{
          borderRadius: "20px",
          p: { xs: 2.5, sm: 3, md: 4 },
          mb: 2,
          backgroundColor: "white",
          boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
        }}
      >
        <Skeleton variant="text" width="40%" height={20} sx={{ mb: 2 }} />
        <Skeleton variant="text" width="50%" height={18} />
        <Skeleton variant="text" width="80%" height={16} sx={{ mt: 0.5 }} />
        <Skeleton variant="text" width="60%" height={16} sx={{ mt: 0.5 }} />
      </Paper>

      {/* Order Items Skeleton */}
      <Paper
        elevation={2}
        sx={{
          borderRadius: "20px",
          p: { xs: 2.5, sm: 3, md: 4 },
          backgroundColor: "white",
          boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
        }}
      >
        <Skeleton variant="text" width="40%" height={20} sx={{ mb: 2 }} />

        {/* First item skeleton */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "row", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            gap: 2,
            p: 2,
            border: "1px solid #eee",
            borderRadius: "16px",
            mb: 2,
          }}
        >
          <Skeleton variant="rectangular" width={72} height={72} sx={{ borderRadius: "8px", flexShrink: 0 }} />
          <Box sx={{ flexGrow: 1 }}>
            <Skeleton variant="text" width="70%" height={18} />
            <Skeleton variant="text" width="30%" height={14} sx={{ mt: 0.5 }} />
          </Box>
          <Skeleton variant="text" width={50} height={18} />
        </Box>

        {/* Second item skeleton */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "row", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            gap: 2,
            p: 2,
            border: "1px solid #eee",
            borderRadius: "16px",
            mb: 2,
          }}
        >
          <Skeleton variant="rectangular" width={72} height={72} sx={{ borderRadius: "8px", flexShrink: 0 }} />
          <Box sx={{ flexGrow: 1 }}>
            <Skeleton variant="text" width="60%" height={18} />
            <Skeleton variant="text" width="25%" height={14} sx={{ mt: 0.5 }} />
          </Box>
          <Skeleton variant="text" width={45} height={18} />
        </Box>

        <Divider sx={{ my: 2, borderColor: "#e5e5e5" }} />

        {/* Totals Skeleton */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", gap: 2, width: "100%", justifyContent: "space-between" }}>
            <Skeleton variant="text" width="15%" height={14} />
            <Skeleton variant="text" width="20%" height={14} />
          </Box>
          <Box sx={{ display: "flex", gap: 2, width: "100%", justifyContent: "space-between" }}>
            <Skeleton variant="text" width="10%" height={14} />
            <Skeleton variant="text" width="15%" height={14} />
          </Box>
          <Divider sx={{ width: "100%", borderColor: "#e5e5e5", my: 1 }} />
          <Box sx={{ display: "flex", gap: 2, width: "100%", justifyContent: "space-between" }}>
            <Skeleton variant="text" width="12%" height={20} />
            <Skeleton variant="text" width="18%" height={20} />
          </Box>
        </Box>
      </Paper>
    </Container>
  );

  if (loading) {
    return <OrderDetailsSkeleton />;
  }

  if (error || !order) {
    return (
      <Container
        maxWidth="md"
        sx={{
          py: { xs: 4, md: 6 },
          px: { xs: 2, sm: 3 },
          color: "black",
          fontFamily: "Rubik, sans-serif",
        }}
      >
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
          sx={{
            mb: 3,
            textTransform: "none",
            color: "black",
            borderRadius: "30px",
            border: "1px solid black",
            px: 2.5,
            py: 1,
            fontWeight: 500,
            "&:hover": { bgcolor: "black", color: "white" },
            transition: "0.3s ease",
          }}
        >
          Back to Orders
        </Button>
        <Alert severity="error">{error || "Order not found"}</Alert>
      </Container>
    );
  }

  const handleMenuOpen = (event, type, item = null) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuType(type);
    setSelectedItem(item);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuType(null);
  };

  const handleOpenCancelModal = () => {
    handleMenuClose();
    setCancelModal({ open: true, reason: "" });
  };

  const handleCloseCancelModal = () => {
    setCancelModal((prev) => ({ ...prev, open: false }));
    setSelectedItem(null);
  };

  const handleConfirmCancel = async () => {
    try {
      if (!cancelModal.reason.trim()) return;
      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";
      if (!token) {
        setError("Please login to cancel orders");
        return;
      }
      await postData(
        `order/cancel/${id}`,
        { reason: cancelModal.reason },
        token,
        false
      );
      setCancelModal({ open: false, reason: "" });
      fetchOrderDetails();
    } catch (err) {
      console.error("Failed to cancel order:", err);
      setError(err.message || "Failed to cancel order");
    }
  };

  const handleOpenReturnModal = () => {
    handleMenuClose();
    setReturnModal({ open: true, reason: "", video: null });
  };

  const handleCloseReturnModal = () => {
    setReturnModal((prev) => ({ ...prev, open: false }));
    setSelectedItem(null);
  };

  const handleConfirmReturn = async () => {
    try {
      if (!returnModal.reason.trim() || !returnModal.video) return;

      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";
      if (!token) {
        setError("Please login to return orders");
        return;
      }

      const formData = new FormData();
      const productToReturn = selectedItem || (order.items.length > 0 ? order.items[0] : null);
      if (!productToReturn || !productToReturn.productId) {
        setError("No product found to return");
        return;
      }

      formData.append("productId", productToReturn.productId);
      formData.append("returnReason", returnModal.reason);
      formData.append("return_video", returnModal.video);

      await postFileUpload(`order/return/${id}`, formData, token);

      setReturnModal({ open: false, reason: "", video: null });
      fetchOrderDetails();
    } catch (err) {
      console.error("Failed to return order:", err);
      // Error handling is managed by postFileUpload toast usually, or we can set error state
    }
  };

  return (
    <Container
      maxWidth="md"
      sx={{
        py: { xs: 3, md: 6 },
        px: { xs: 2, sm: 3 },
        color: "black",
        fontFamily: "Rubik, sans-serif",
      }}
    >
      {/* Back Button */}
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate(-1)}
        sx={{
          mb: { xs: 2.5, md: 3 },
          textTransform: "none",
          color: "black",
          borderRadius: "30px",
          border: "1px solid black",
          px: 2.5,
          py: 1,
          fontWeight: 500,
          "&:hover": { bgcolor: "black", color: "white" },
          transition: "0.3s ease",
          fontSize: { xs: "13px", sm: "14px" },
        }}
      >
        Back to Orders
      </Button>

      {/* Order Summary */}
      <Paper
        elevation={2}
        sx={{
          borderRadius: "20px",
          backgroundColor: "white",
          p: { xs: 2.5, sm: 3, md: 4 },
          mb: 2,
          boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "row", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            gap: 2,
            mb: 3,
          }}
        >
          {/* Left Section */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexGrow: 1,
            }}
          >
            <Avatar
              sx={{
                bgcolor: "black",
                width: 48,
                height: 48,
                color: "white",
              }}
            >
              {getStatusIcon(order.status)}
            </Avatar>

            <Box>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ fontSize: { xs: "16px", sm: "18px" } }}
              >
                Order {order.id}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#666", fontSize: { xs: "12px", sm: "13px" } }}
              >
                Placed on {order.date}
              </Typography>
            </Box>
          </Box>

          {/* cancel Menu */}
          {order.status !== "delivered" && order.status !== "cancelled" && (
            <IconButton
              onClick={(e) => handleMenuOpen(e, "order")}
              sx={{ color: "#444", alignSelf: { xs: "flex-end", sm: "center" } }}
            >
              <MoreVertIcon />
            </IconButton>
          )}
        </Box>

        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
        >
          {menuType === "order" && (
            <MenuItem onClick={handleOpenCancelModal} sx={{ color: "red" }}>
              Cancel Order
            </MenuItem>
          )}
          {menuType === "item" && (
            <>
              <MenuItem startIcon={<KeyboardReturnIcon />} onClick={handleOpenReturnModal} sx={{ color: "blue" }}>
                Return Product
              </MenuItem>
            </>
          )}
        </Menu>

        <Divider sx={{ borderColor: "#e5e5e5", mb: 3 }} />

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ fontSize: { xs: "13px", sm: "14px" } }}
            >
              Status
            </Typography>
            <Chip
              label={order.status.toUpperCase()}
              sx={{
                fontWeight: 600,
                color: "white",
                bgcolor: "black",
                borderRadius: "18px",
                px: 1.5,
                mt: 1,
                fontSize: { xs: "11px", sm: "12px" },
              }}
            />
          </Grid>
          {order.invoiceNumber && (
            <Grid item xs={12} sm={6} sx={{ textAlign: { sm: "right" }, paddingTop: "28px" }}>
              <Button
                variant="outlined"
                startIcon={<GetApp />}
                onClick={handleDownloadInvoice}
                disabled={downloading}
                sx={{
                  color: "black",
                  borderColor: "black",
                  borderRadius: "20px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: { xs: "12px", sm: "13px" },
                  "&:hover": {
                    bgcolor: "black",
                    color: "white",
                    borderColor: "black",
                  },
                }}
              >
                {downloading ? "Downloading..." : "Download Invoice"}
              </Button>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Stepper */}
      <Paper
        elevation={2}
        sx={{
          borderRadius: "20px",
          p: { xs: 2, sm: 3, md: 4 },
          mb: 2,
          backgroundColor: "white",
          boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight={600}
          sx={{ mb: 2, fontSize: { xs: "14px", sm: "15px" } }}
        >
          Order Progress
        </Typography>

        <Stepper
          activeStep={order.activeStep}
          orientation={isMobile ? "vertical" : "horizontal"}
          alternativeLabel={!isMobile}
          sx={{
            "& .MuiStepLabel-label": {
              color: "#555",
              fontSize: { xs: "12px", md: "14px" },
              textAlign: isMobile ? "left" : "center",
            },
            "& .MuiStepIcon-root": { color: "#bbb" },
            "& .MuiStepIcon-root.Mui-active": { color: "black" },
            "& .MuiStepIcon-root.Mui-completed": { color: "black" },
            ...(isMobile && {
              alignItems: "flex-start",
              pl: 1,
              "& .MuiStepConnector-line": {
                minHeight: 24,
              },
            }),
          }}
        >
          {order.steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Shipping Info */}
      <Paper
        elevation={2}
        sx={{
          borderRadius: "20px",
          p: { xs: 2.5, sm: 3, md: 4 },
          mb: 2,
          backgroundColor: "white",
          boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight={600}
          sx={{ mb: 2, fontSize: { xs: "14px", sm: "15px" } }}
        >
          Shipping Information
        </Typography>
        <Typography
          variant="body1"
          fontWeight={500}
          sx={{ fontSize: { xs: "14px", sm: "15px" } }}
        >
          {order.shippingAddress.name}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "#666", fontSize: { xs: "13px", sm: "14px" } }}
        >
          {order.shippingAddress.address}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "#666", mt: 0.5, fontSize: { xs: "13px", sm: "14px" } }}
        >
          Phone:{" "}
          {order.shippingAddress.phone
            ? `+91 ${order.shippingAddress.phone}`
            : "N/A"}
        </Typography>
      </Paper>

      {/* Order Items */}
      <Paper
        elevation={2}
        sx={{
          borderRadius: "20px",
          p: { xs: 2.5, sm: 3, md: 4 },
          backgroundColor: "white",
          boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight={600}
          sx={{ mb: 2, fontSize: { xs: "14px", sm: "15px" } }}
        >
          Order Items
        </Typography>

        {order.items.map((item, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "column" },
              gap: 2,
              p: 2,
              border: "1px solid #eee",
              borderRadius: "16px",
              mb: 2,
              transition: "0.3s ease",
            }}
          >
            {/* Item Details */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "row", sm: "row" },
                alignItems: { xs: "flex-start", sm: "center" },
                gap: 2,
              }}
            >
              <Avatar
                src={item.image}
                variant="rounded"
                sx={{
                  width: { xs: 64, sm: 72 },
                  height: { xs: 64, sm: 72 },
                  border: "1px solid #ddd",
                  flexShrink: 0,
                }}
              />
              <Box sx={{ flexGrow: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{ fontSize: { xs: "14px", sm: "15px" } }}
                  >
                    {item.name}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{ color: "#666", fontSize: { xs: "13px", sm: "14px" } }}
                >
                  Qty: {item.quantity}
                </Typography>
              </Box>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{ fontSize: { xs: "14px", sm: "15px" } }}
              >
                ₹{item.price}
              </Typography>
              {item.returnInfo && (
                <Chip
                  label={item.returnInfo.returnStatus.toUpperCase()}
                  size="small"
                  color="warning"
                  sx={{
                    fontSize: "10px",
                    height: "20px",
                    fontWeight: 700,
                    borderRadius: "4px",
                  }}
                />
              )}
              {/* return product  Menu */}
              {order.status === "delivered" && !item.returnInfo && (
                <IconButton
                  onClick={(e) => handleMenuOpen(e, "item", item)}
                  sx={{ color: "#444", alignSelf: { xs: "flex-end", sm: "center" } }}
                >
                  <MoreVertIcon />
                </IconButton>
              )}
            </Box>

            {/* Review & Arrow Section */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1 }}>
              {item.review ? (
                <Box sx={{ px: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        sx={{
                          fontSize: "16px",
                          color: star <= item.review.rating ? "#FFB800" : "#e0e0e0",
                        }}
                      />
                    ))}
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{ color: "#444", fontStyle: "italic", fontSize: "13px" }}
                  >
                    "{item.review.review}"
                  </Typography>
                </Box>
              ) : (
                order.status === "delivered" && (
                  <Button
                    variant="outlined"
                    startIcon={<Star />}
                    size="small"
                    onClick={() => handleOpenReview(id, item)}
                    sx={{
                      color: "black",
                      textTransform: "none",
                      border: "none",
                      px: 2.5,
                      textDecoration: "underline",
                      alignSelf: "flex-start",
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Rate & Review
                  </Button>
                )
              )}
              {item.returnInfo && (
                <Button
                  size="small"
                  onClick={() => setExpandedReturnId(expandedReturnId === item.productId ? null : item.productId)}
                  endIcon={expandedReturnId === item.productId ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                  sx={{ textTransform: "none", color: "#fa1a1a" }}
                >
                  Return Details
                </Button>
              )}
            </Box>

            {/* Return Details Expandable Section */}
            {item.returnInfo && (
              <Collapse in={expandedReturnId === item.productId} timeout="auto" unmountOnExit>
                <Box sx={{ mt: 2, p: 2, bgcolor: "#fff5f5", borderRadius: "12px", border: "1px solid #ffebeb" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: "black" }}>
                    Return Request Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Return ID</Typography>
                      <Typography variant="body2" fontWeight={500}>{item.returnInfo.returnId}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Return Status</Typography>
                      <Typography variant="body2" fontWeight={500} sx={{ color: "#fa1a1a" }}>
                        {item.returnInfo.returnStatus.toUpperCase()}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Reason</Typography>
                      <Typography variant="body2" fontWeight={500}>{item.returnInfo.returnReason.replace('_', ' ')}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Initiated At</Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {new Date(item.returnInfo.requestedAt).toLocaleDateString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Refund Status</Typography>
                      <Typography variant="body2" fontWeight={500} sx={{ color: item.returnInfo.refundStatus === 'completed' ? 'green' : '#666' }}>
                        {item.returnInfo.refundStatus?.replace('_', ' ').toUpperCase() || 'N/A'}
                      </Typography>
                    </Grid>
                    {item.returnInfo.returnVideo && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>Return Proof Video</Typography>
                        <Box sx={{ height: "200px", width: "200px", borderRadius: "8px", overflow: "hidden", border: "1px solid #ddd" }}>
                          <video
                            src={item.returnInfo.returnVideo}
                            style={{ height: "200px", width: "200px", display: "block" }}
                            controls
                          />
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </Collapse>
            )}
          </Box>
        ))}

        <Divider sx={{ my: 2, borderColor: "#e5e5e5" }} />

        {/* Totals */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 1,
          }}
        >
          {order.subtotal && (
            <Box
              sx={{
                display: "flex",
                gap: 2,
                width: "100%",
                justifyContent: "space-between",
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: "#666", fontSize: { xs: "13px", sm: "14px" } }}
              >
                Subtotal:
              </Typography>
              <Typography
                variant="body2"
                fontWeight={500}
                sx={{ fontSize: { xs: "13px", sm: "14px" } }}
              >
                ₹{order.subtotal}
              </Typography>
            </Box>
          )}

          {order.gsttotal && (
            <Box
              sx={{
                display: "flex",
                gap: 2,
                width: "100%",
                justifyContent: "space-between",
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: "#666", fontSize: { xs: "13px", sm: "14px" } }}
              >
                GST:
              </Typography>
              <Typography
                variant="body2"
                fontWeight={500}
                sx={{ fontSize: { xs: "13px", sm: "14px" } }}
              >
                ₹{order.gsttotal}
              </Typography>
            </Box>
          )}

          {order.deliveryCharges && (
            <Box
              sx={{
                display: "flex",
                gap: 2,
                width: "100%",
                justifyContent: "space-between",
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: "#666", fontSize: { xs: "13px", sm: "14px" } }}
              >
                Delivery Charges:
              </Typography>
              <Typography
                variant="body2"
                fontWeight={500}
                sx={{ fontSize: { xs: "13px", sm: "14px" } }}
              >
                ₹{order.deliveryCharges}
              </Typography>
            </Box>
          )}

          {order.couponDiscount &&
            parseFloat(order.couponDiscount) > 0 && (
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  width: "100%",
                  justifyContent: "space-between",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: "#666", fontSize: { xs: "13px", sm: "14px" } }}
                >
                  Discount:
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={500}
                  sx={{ color: "green", fontSize: { xs: "13px", sm: "14px" } }}
                >
                  -₹{order.couponDiscount}
                </Typography>
              </Box>
            )}

          <Divider
            sx={{ width: "100%", borderColor: "#e5e5e5", my: 1 }}
          />

          <Box
            sx={{
              display: "flex",
              gap: 2,
              width: "100%",
              justifyContent: "space-between",
            }}
          >
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{ fontSize: { xs: "15px", sm: "16px" } }}
            >
              Total:
            </Typography>
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{ fontSize: { xs: "15px", sm: "16px" } }}
            >
              ₹{order.total}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Cancel Dialog */}
      <Dialog
        open={cancelModal.open}
        onClose={handleCloseCancelModal}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontSize: { xs: "16px", sm: "18px" } }}>
          Cancel Order
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Reason"
            type="text"
            fullWidth
            value={cancelModal.reason}
            onChange={(e) =>
              setCancelModal({ open: true, reason: e.target.value })
            }
            InputLabelProps={{
              sx: { fontSize: { xs: "13px", sm: "14px" } },
            }}
            inputProps={{
              style: { fontSize: 13 },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2.5 }}>
          <Button
            onClick={handleCloseCancelModal}
            color="inherit"
            sx={{ fontSize: { xs: "13px", sm: "14px" } }}
          >
            Close
          </Button>
          <Button
            onClick={handleConfirmCancel}
            variant="contained"
            disabled={!cancelModal.reason.trim()}
            sx={{
              backgroundColor: "black",
              color: "white",
              fontSize: { xs: "13px", sm: "14px" },
              "&:hover": { backgroundColor: "#222" },
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Return Dialog */}
      <Dialog
        open={returnModal.open}
        onClose={handleCloseReturnModal}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontSize: { xs: "16px", sm: "18px" } }}>
          Return Order
        </DialogTitle>
        <DialogContent>
          <TextField
            select
            autoFocus
            margin="dense"
            label="Return Reason"
            fullWidth
            value={returnModal.reason}
            onChange={(e) =>
              setReturnModal((prev) => ({ ...prev, reason: e.target.value }))
            }
            InputLabelProps={{
              sx: { fontSize: { xs: "13px", sm: "14px" } },
            }}
            inputProps={{
              style: { fontSize: 13 },
            }}
            sx={{ mb: 2 }}
          >
            {[
              { value: "damaged", label: "Damaged" },
              { value: "wrong_item", label: "Wrong Item" },
              { value: "quality_issue", label: "Quality Issue" },
              { value: "other", label: "Other" },
            ].map((option) => (
              <MenuItem key={option.value} value={option.value} sx={{ fontSize: 16 }}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <Button
            variant="outlined"
            component="label"
            fullWidth
            sx={{
              textTransform: "none",
              fontSize: { xs: "13px", sm: "14px" },
              borderColor: "#ddd",
              color: "#666",
              justifyContent: "flex-start",
              pl: 1,
            }}
          >
            Upload Return Video
            <input
              type="file"
              hidden
              accept="video/*"
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  setReturnModal((prev) => ({
                    ...prev,
                    video: e.target.files[0],
                  }));
                }
              }}
            />
          </Button>
          {returnModal.video && (
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                bgcolor: "#f9f9f9",
                borderRadius: "8px",
                border: "1px solid #eee",
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: "#666", display: "block", mb: 0.5 }}
              >
                Selected Video:
              </Typography>
              <Typography
                variant="body2"
                noWrap
                title={returnModal.video.name}
                sx={{ fontWeight: 500, mb: 1 }}
              >
                {returnModal.video.name}
              </Typography>
              <Box
                sx={{
                  width: "100%",
                  height: 150,
                  bgcolor: "#000",
                  borderRadius: "8px",
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <video
                  width="100%"
                  height="100%"
                  controls
                  src={URL.createObjectURL(returnModal.video)}
                  style={{ objectFit: "contain" }}
                />
              </Box>
            </Box>
          )}

        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2.5 }}>
          <Button
            onClick={handleCloseReturnModal}
            color="inherit"
            sx={{ fontSize: { xs: "13px", sm: "14px" } }}
          >
            Close
          </Button>
          <Button
            onClick={handleConfirmReturn}
            variant="contained"
            disabled={!returnModal.reason.trim() || !returnModal.video}
            sx={{
              backgroundColor: "black",
              color: "white",
              fontSize: { xs: "13px", sm: "14px" },
              "&:hover": { backgroundColor: "#222" },
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rate & Review Modal */}
      <ReviewModal
        open={reviewModal.open}
        orderId={reviewModal.orderId}
        onClose={handleCloseReview}
        onSubmit={handleSubmitReview}
      />
    </Container >
  );
}

export default OrderDetails;
